<template>

    <v-container>

        <slide-up-down :active="!isForgotMode" :duration="500">
            <v-card-title class="pa-0 pb-6">
                <span class="text-h4">Sign in</span>
            </v-card-title>
            <v-form ref="signInForm" lazy-validation @keyup.native.enter="signIn">
                <v-text-field v-model="signInRequest.emailAddress" :rules="[v => !!v || 'Please enter your email address']" label="Email Address" type="email" outlined required hide-details="auto" class="mb-3" />
                <v-text-field v-model="signInRequest.password" :rules="[v => !!v || 'Please enter your password']" label="Password" type="password" outlined required hide-details="auto" class="mb-3" />
            </v-form>
            <v-card-actions class="mt-6 px-0">
                <a @click.prevent="toggleMode">Forgot your password?</a>
                <v-spacer />
                <api-button @click="signIn()" large busyEventName="sign-in" class="px-3"><v-icon class="mr-2">mdi-login</v-icon>Sign In</api-button>
            </v-card-actions>
            <v-alert class="mt-6" :value="isMessage" border="top" type="error" transition="scale-transition">{{ messageText }}</v-alert>
        </slide-up-down>

        <slide-up-down :active="isForgotMode" :duration="500">
            <v-card-title class="pa-0 pb-6">
                <span class="text-h4">Forgot your password?</span>
            </v-card-title>
            <v-card-text class="pa-0 pb-6">Enter the email address you're registered with below and we'll send you and email to reset it.</v-card-text>
            <v-form ref="forgotPasswordForm" lazy-validation @keyup.native.enter="submitForgot">
                <v-text-field v-model="forgotRequest.emailAddress" :rules="[v => !!v || 'Please enter your email address']" label="Email Address" type="email" outlined required hide-details="auto" class="mb-3" />
            </v-form>
            <v-card-actions class="mt-6 px-0">
                <a @click.prevent="toggleMode">Go back to sign in</a>
                <v-spacer />
                <api-button @click="submitForgot()" busyEventName="submit-forgot" large class="px-4"><v-icon class="mr-2">mdi-send</v-icon>Submit</api-button>
            </v-card-actions>
            <v-alert class="mt-6" :value="isMessage" border="top" :type="messageType" transition="scale-transition">{{ messageText }}</v-alert>
        </slide-up-down>

    </v-container>

</template>

<script lang="ts">
    import Vue from "vue";
    import Component from "vue-class-component";
    import SlideUpDown from "vue-slide-up-down";
    import { Ref } from "vue-property-decorator";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import { User, IUser } from "@/model/User";
    import authentication from "@/utilities/Authentication";
    import apiClient from "@/utilities/ApiClient";
    import { VForm } from "@/typedefs/types";
import router from "@/router/router";
   
    // We could put these interfaces (or classes) in their own files - but they're small 
    // and so far they're only used here.

    interface ISignInRequest {
        emailAddress: string;
        password: string;
    }

    export interface ISignInResponse {
        isSuccess: boolean;
        message: string;
        user: IUser;
    }

    interface IForgotRequest {
        emailAddress: string;
    }

    interface IForgotResponse {
        isSuccess: boolean;
        message: string;
    }

    // https://vuejs.org/v2/guide/typescript.html

    @Component({
        components: { SlideUpDown, ApiButton }
    })     
    export default class SignInForm extends Vue {

        // Lifecycle hook
        mounted() {
            if (router.currentRoute.query.email != null) {
                const email = decodeURI(router.currentRoute.query.email.toString())
                this.signInRequest.emailAddress = email;
                this.forgotRequest.emailAddress = email;
                router.push("/")
            }
            else {
                this.loadFromStorage()
            }            
        }

        @Ref("signInForm") private readonly signInForm!: VForm;
        @Ref("forgotPasswordForm") private readonly forgotPasswordForm!: VForm;

        signInRequest: ISignInRequest = { emailAddress: "", password: "" };
        forgotRequest: IForgotRequest = { emailAddress: "" };

        isForgotMode = false;
        messageText = "";
        messageType = "error"

        get isMessage(): boolean {
            return !!this.messageText;
        }

        // get properties are 'computeds'
        get isAuthenticated(): boolean {
            return this.$store.state.signedInUser !== null;
        }

        get userName(): string {
            const user = this.$store.state.signedInUser as User;
            if (user === null) return "not logged in";
            return `${user.forename} ${user.surname}`;
        }

        toggleMode() {
            this.signInForm.resetValidation();
            this.forgotPasswordForm.resetValidation();
            this.messageText = "";
            this.messageType = "error";
            if (!this.isForgotMode && this.signInRequest.emailAddress) {
                // copy the email over to save user typing it out again
                this.forgotRequest.emailAddress = this.signInRequest.emailAddress;
            }
            this.isForgotMode = !this.isForgotMode;
        }

        async signIn() {
            // TODO - if this triggered from enter key, we could do with finding anad passing the button...
            if (!this.signInForm.validate()) {
                this.messageText = "Please enter your email address and password!";
                return;
            }

            const response: ISignInResponse = await apiClient.post("/Api/Authentication/signin", this.signInRequest, "sign-in");
            await authentication.signIn(new User(response.user));

            if (this.isAuthenticated) {
                this.saveToStorage(response.user);
                console.log("...SignInForm component - emitting is-authenticated");
                this.$emit("is-authenticated");
            }
            else {
                this.messageText = response.message;
            }
        }

        async submitForgot() {
            if (!this.forgotPasswordForm.validate()) return;

            const response: IForgotResponse = await apiClient.post("/Api/Authentication/forgot", this.forgotRequest, "submit-forgot");
            this.messageText = response.message;
            this.messageType = response.isSuccess ? "success" : "error"
        }

        private loadFromStorage() {
            if (typeof (Storage) === "undefined") return;
            const userJson = localStorage.getItem("user");
            if (!userJson) return;
            try {
                const user = JSON.parse(userJson);
                this.signInRequest.emailAddress = (user && user.emailAddress) ? user.emailAddress : "";
                this.forgotRequest.emailAddress = this.signInRequest.emailAddress;
            } catch (e) {
                this.signInRequest.emailAddress = "";
            }
        }

        private saveToStorage(user: IUser) {
            if (typeof (Storage) === "undefined") return;
            localStorage.setItem("user", JSON.stringify(user));
        }
        
    }
</script>